import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import CustomSelect from "../../Components/CustomSelect";
import { ClipLoader } from "react-spinners";
import { LANGUAGE_OPTIONS, TYPE_OPTIONS } from "services/constants";
import localization from "services/localization";
import useLanguage from "services/useLanguage";
import IconPicker from "views/Components/IconPicker";

const AddEditPromptModal = ({
  show,
  title,
  formData,
  errors,
  onChange,
  onSubmit,
  onClose,
  loading,
  promptCategories = [],
  templates = [],
  onFormChange,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGE_OPTIONS[0]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const currentLang = useLanguage();

  const TYPE_LABEL_MAP = TYPE_OPTIONS.reduce((acc, t) => {
    acc[t.value] = t.label;
    return acc;
  }, {});

  const categoryOptions = promptCategories.reduce((acc, c) => {
    let group = acc.find((g) => g.type === c.type);

    const option = {
      value: c.id,
      label:
        c.title?.[currentLang] ||
        c.title?.It ||
        c.title?.En ||
        "Unnamed Prompt",
    };

    if (!group) {
      group = {
        type: c.type,
        label: TYPE_LABEL_MAP[c.type] || c.type,
        options: [],
      };
      acc.push(group);
    }

    group.options.push(option);
    return acc;
  }, []);

  const templateOptions = templates.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  useEffect(() => {
    if (formData?.prompt_category_id) {
      const allOptions = categoryOptions.flatMap((group) => group.options);
      const found = allOptions.find(
        (c) => c.value === formData.prompt_category_id,
      );
      setSelectedCategory(found || null);
    }

    if (formData?.prompt_template_id) {
      const foundT = templateOptions.find(
        (t) => t.value === formData.prompt_template_id,
      );
      setSelectedTemplate(foundT || null);
    }
  }, [formData, promptCategories, templates]);

  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
  };

  const handleCategoryChange = (item) => {
    setSelectedCategory(item);
    onChange({
      target: { name: "prompt_category_id", value: item?.value || "" },
    });
  };

  const handleTemplateChange = (item) => {
    setSelectedTemplate(item);
    onChange({
      target: { name: "prompt_template_id", value: item?.value || "" },
    });
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      className="themed-modal addEditModal"
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={onSubmit}>
        <Modal.Body>
          {/* LANGUAGE SELECTOR */}
          <div className="mb-3">
            <Form.Label className="bootstrap-label">
              {localization.language || "Language"}
            </Form.Label>
            <CustomSelect
              options={LANGUAGE_OPTIONS}
              value={selectedLanguage}
              onChange={handleLanguageChange}
              className="w-40"
            />
          </div>

          <Row>
            {/* TITLE */}
            <Col md={12} className="mb-3">
              <Form.Group className="required">
                <Form.Label className="bootstrap-label">
                  {" "}
                  {localization.title_label || "Title"} (
                  {selectedLanguage.value})
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData?.title?.[selectedLanguage.value] || ""}
                  placeholder={`${
                    localization.title_placeholder || "Enter title"
                  } (${selectedLanguage.value})`}
                  onChange={(e) =>
                    onChange({
                      target: {
                        name: "title",
                        value: {
                          ...formData.title,
                          [selectedLanguage.value]: e.target.value,
                        },
                      },
                    })
                  }
                  isInvalid={!!errors.title}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.title}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* DESCRIPTION */}
            <Col md={12} className="mb-3">
              <Form.Group className="required">
                <Form.Label className="bootstrap-label">
                  {localization.description_label || "Description"} (
                  {selectedLanguage.value})
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData?.description?.[selectedLanguage.value] || ""}
                  placeholder={`${
                    localization.description_placeholder || "Enter description"
                  } (${selectedLanguage.value})`}
                  onChange={(e) =>
                    onChange({
                      target: {
                        name: "description",
                        value: {
                          ...formData.description,
                          [selectedLanguage.value]: e.target.value,
                        },
                      },
                    })
                  }
                  isInvalid={!!errors.description}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* PROMPT INSTRUCTION */}
            {/* <Col md={12} className="mb-3">
              <Form.Group className="required">
                <Form.Label>Prompt Instruction</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.prompt_instruction || ""}
                  onChange={(e) =>
                    onChange({
                      target: {
                        name: "prompt_instruction",
                        value: e.target.value,
                      },
                    })
                  }
                  placeholder="Enter instruction text..."
                  isInvalid={!!errors.prompt_instruction}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.prompt_instruction}
                </Form.Control.Feedback>
              </Form.Group>
            </Col> */}

            {/* CATEGORY SELECT */}
            <Col md={6} className="mb-3">
              <Form.Group className="required">
                <Form.Label className="bootstrap-label">
                  {localization.prompt_category || "Prompt Category"}
                </Form.Label>
                <CustomSelect
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  placeholder={
                    localization.select_category || "Select category"
                  }
                />
                {errors.prompt_category_id && (
                  <div className="text-danger small mt-1">
                    {errors.prompt_category_id}
                  </div>
                )}
              </Form.Group>
            </Col>

            {/* TEMPLATE SELECT */}
            <Col md={6} className="mb-3">
              <Form.Group className="required">
                <Form.Label className="bootstrap-label">
                  {localization.template || "Template"}
                </Form.Label>
                <CustomSelect
                  options={templateOptions}
                  value={selectedTemplate}
                  onChange={handleTemplateChange}
                  placeholder={
                    localization.select_template || "Select template"
                  }
                />
                {errors.prompt_template_id && (
                  <div className="text-danger small mt-1">
                    {errors.prompt_template_id}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label className="bootstrap-label">
                  {localization.icon || "Icon"}
                </Form.Label>
                <IconPicker
                  value={formData.icon}
                  onChange={(value) => onFormChange("icon", value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button className="action-btn secondary" onClick={onClose}>
            {localization.cancel || "Cancel"}
          </Button>
          <Button
            className="action-btn primary no-border"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <ClipLoader color="var(--primary-icon)" size={20} />
            ) : (
              localization.save || "Save"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddEditPromptModal;
